import { expenseStore } from "../expenses";

// Wave 14 — vendor-history prefill. When a user has spent money with a
// vendor before, we can almost always guess the right category + tax
// policy for the next expense with that vendor by taking the mode of
// their past choices. This avoids the third-and-fourth "Software" pick
// every time a Stripe invoice comes in.

export interface VendorDefaults {
  categoryId: string | null;
  categoryLabel: string | null;
  taxPolicyId: string | null;
  taxPolicyLabel: string | null;
}

/**
 * Return the most-common categoryId + taxPolicyId for the given vendor,
 * computed across the org's expense history. Returns null if the vendor
 * has no prior expenses (caller should skip prefill).
 *
 * Fields with no clear mode stay null — we don't guess when half the
 * expenses are one category and half are another.
 */
export function vendorDefaults(vendorId: string): VendorDefaults | null {
  if (!vendorId) return null;

  const expenses = expenseStore.list().filter((e) => e.vendorId === vendorId);
  if (expenses.length === 0) return null;

  const catMode = modeWithLabel(
    expenses.map((e) => ({
      id: e.categoryId,
      label: e.categoryLabel ?? null,
    })),
  );
  const taxMode = modeWithLabel(
    expenses.map((e) => ({
      id: e.taxPolicyId,
      label: e.taxPolicyLabel ?? null,
    })),
  );

  // If neither prefill is available, return null so the caller can skip
  // the whole chip-render path. Keeps the dialog clean when we truly have
  // nothing to suggest.
  if (!catMode && !taxMode) return null;

  return {
    categoryId: catMode?.id ?? null,
    categoryLabel: catMode?.label ?? null,
    taxPolicyId: taxMode?.id ?? null,
    taxPolicyLabel: taxMode?.label ?? null,
  };
}

interface Entry {
  id: string | null;
  label: string | null;
}

/**
 * Most-frequent non-null id. Ignores entries with a null id so an expense
 * that was saved without a category doesn't "win" by default. Returns
 * null if every entry has a null id, or if the top count is a tie — a
 * tie means we don't have a signal worth prefilling.
 */
function modeWithLabel(entries: Entry[]): Entry | null {
  const counts = new Map<string, { count: number; label: string | null }>();
  for (const e of entries) {
    if (!e.id) continue;
    const prev = counts.get(e.id);
    if (prev) {
      prev.count += 1;
      if (!prev.label && e.label) prev.label = e.label;
    } else {
      counts.set(e.id, { count: 1, label: e.label });
    }
  }

  if (counts.size === 0) return null;

  const ranked = [...counts.entries()].sort(
    (a, b) => b[1].count - a[1].count,
  );
  // Tie guard: if the top two have the same count we don't have a clear
  // winner, so don't prefill at all.
  if (ranked.length > 1 && ranked[0][1].count === ranked[1][1].count) {
    return null;
  }
  const [id, { label }] = ranked[0];
  return { id, label };
}
